import {useCallback, useEffect} from 'react';

import {MAX_CANVAS_OBJECTS} from 'config';
import {FabricImage, FabricObject, FabricText, util} from 'fabric';
import {useWindowDimensions} from 'hooks';
import {usePrefsStore} from 'store';

import * as backgrounds from '../assets/backgrounds';

import {useAction} from './canvas-actions';
import {migrate5to6, migrate6to7} from './migrations';
import {setScaling} from './object-helper';
import {ArrowLayoutManager, customControlVisibility} from './useCustomControls';

// 0,614379085
// 1,627659574
// export const CANVAS_WIDTH = 940;
// export const CANVAS_HEIGHT = 1530;
export const CANVAS_WIDTH = 1080;
export const CANVAS_HEIGHT = 1613; // old:1758 new:1613

export const CANVAS_RATIO = CANVAS_HEIGHT / CANVAS_WIDTH;
export const DRAWING_WIDTH = 5;
export const DEFAULT_COLOR = '#000000';
export const DRAWING_MODE = 'draw';
export const SELECTION_MODE = 'select';

export const setStrokeColor = (canvas, color) => {
    canvas.freeDrawingBrush.color = color;
};

export const setStrokeWidth = (canvas, width) => {
    canvas.freeDrawingBrush.width = width;
};

export const setFabricDefaults = () => {
    FabricObject.ownDefaults.selectionBackgroundColor = 'rgba(0,0,0,0.15)';
    FabricObject.ownDefaults.hasBorders = true;
    FabricObject.ownDefaults.borderColor = 'rgba(255,255,255,0.5)';
    FabricObject.ownDefaults.borderScaleFactor = 0.5;
    FabricObject.ownDefaults.padding = 3;
    FabricObject.customProperties = ['custom'];

    // Patch FabricText.prototype.toObject to guard against undefined text
    const originalToObject = FabricText.prototype.toObject;
    FabricText.prototype.toObject = function(propertiesToInclude) {
        if (this.text === undefined || this.text === null) {
            this.text = '';
        }

        return originalToObject.call(this, propertiesToInclude);
    };
};

export const setLastObjectActive = (canvas) => {
    const objects = canvas.getObjects();
    if (objects.length > 0) {
        canvas.setActiveObject(objects[objects.length - 1]);
    }
};

export const discardActiveObject = (canvas) => {
    if (canvas?.getActiveObject()) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
    }
};

export const clear = (canvas, background) => {
    if (!canvas) {
        return;
    }

    canvas.clear();
    setBackground(canvas, background);
};

export const setBackground = (canvas, background, onComplete) => {
    if (backgrounds[background]) {
        FabricImage.fromURL(backgrounds[background]).then((img) => {
            // v7: defaults to center/center, must set left/top for background
            img.set({originX: 'left', originY: 'top'});
            img.scaleToWidth(CANVAS_WIDTH);

            canvas.backgroundColor = '#191919';
            canvas.backgroundImage = img;
            canvas.renderAll();
            onComplete && onComplete();
        });

        return background;
    } else {
        if (background === null) {
            canvas.backgroundColor = '#191919';
        } else {
            canvas.backgroundColor = '#ffffff';
        }

        // In Fabric v6, backgroundImage is a property
        canvas.backgroundImage = null;
        canvas.renderAll();
        onComplete && onComplete();

        return null;
    }
};

export const isCustom = (o) => {
    return !!o.custom;
};

export const isCustomType = (o, type) => {
    return o.custom && o.custom.type === type;
};

/**
 * Calculates the dimensions of a canvas based on the window width, window height, and a canvas ratio.
 * This hook also sets then background into the canvas after the background pref has changed.
 * Background must be re-set if dimensions are changing.
 *
 * @param {Object} canvas - The canvas object.
 * @returns {Object} - The calculated dimensions of the canvas.
 */
export const useCalculateDimensions = (canvas) => {
    const background = usePrefsStore(state => state.background);

    const {width: windowWidth, height: windowHeight} = useWindowDimensions();

    const {dispatch} = useAction();

    const calculateDimensions = useCallback((wWidth, wHeight) => {
        let newWidth;
        let newHeight;
        let adjust = 0;
        const preserveHeight = 200; // Preserve height, header+toolbar+footer

        if (wWidth < wHeight) {
            newWidth = wWidth;
            newHeight = newWidth * CANVAS_RATIO;

            if (newHeight + preserveHeight > wHeight) {
                adjust = newHeight + preserveHeight - wHeight;
                newHeight = newHeight - adjust;
                newWidth = newHeight / CANVAS_RATIO;
            }

        } else {
            newHeight = wHeight - preserveHeight;
            newWidth = newHeight / CANVAS_RATIO;

            if (newWidth > wWidth) {
                adjust = newWidth - wWidth;
                newWidth = newWidth - adjust;
                newHeight = newHeight / CANVAS_RATIO;
            }
        }

        newHeight = Math.floor(newHeight);
        newWidth = Math.floor(newWidth);

        const ratio = newWidth / CANVAS_WIDTH;

        console.debug(`ww:${wWidth} wh:${wHeight} cw:${newWidth} ch:${newHeight} adjW:${adjust} ratio:${ratio}`);

        return {width: newWidth, height: newHeight};
    }, []);

    useEffect(() => {
        if (!canvas) {
            return;
        }

        try {
            canvas.setDimensions(calculateDimensions(windowWidth, windowHeight));
        } catch (e) {
            // suppressing error so app can reload without setDimensions error
            console.log('suppress reload getting error');
        }

        setBackground(canvas, background, () => {
            dispatch('push');
        });

        const scaling = getZoom(canvas);
        canvas.setZoom(scaling.zoom);
    }, [background, canvas, calculateDimensions, windowWidth, windowHeight, dispatch]);

    return calculateDimensions(windowWidth, windowHeight);
};

export const getZoom = (canvas) => {
    return {
        zoom: canvas.width / CANVAS_WIDTH,
    };
};

export const limitReached = (canvas) => {
    if (!canvas) {
        return false;
    }

    const drawings = canvas.getObjects().filter(object => object?.path && object.path.length > 1);
    const objectCountMinusDrawings = (canvas.getObjects().length - drawings.length);

    return objectCountMinusDrawings >= MAX_CANVAS_OBJECTS;
};

export const canvasToJson = (canvas) => {
    if (!canvas) {
        return null;
    }

    return canvas.toJSON(['hasControls', 'hasBorders', 'custom']);
};

/**
 * Recursively fix text objects in JSON before Fabric.js creates them
 * This catches nested text objects that Fabric.js will create internally
 */
const fixTextInJson = (json) => {
    if (!json) return;

    // CRITICAL: Remove any text property from non-text objects
    // Fabric.js v6 might try to create text from these properties
    if (json.type !== 'Text' && json.type !== 'Textbox' && json.type !== 'IText') {
        if (json.text !== undefined) {
            console.log('[fixTextInJson] Removing text property from', json.type);
            delete json.text;
        }
    }

    // Fix this object if it's a text object
    if (json.type === 'Text' || json.type === 'Textbox' || json.type === 'IText') {
        console.log('[fixTextInJson] Found text object, text=', json.text);
        if (json.text === undefined || json.text === null) {
            console.log('[fixTextInJson] Setting empty text');
            json.text = '';
        } else if (typeof json.text !== 'string') {
            console.log('[fixTextInJson] Converting text to string');
            json.text = String(json.text);
        }
    }

    // Recursively fix nested objects in groups
    if (json.objects && Array.isArray(json.objects)) {
        console.log('[fixTextInJson] Found group with', json.objects.length, 'nested objects');
        for (let i = 0; i < json.objects.length; i++) {
            console.log('[fixTextInJson] Fixing nested object', i, ':', json.objects[i].type);
            fixTextInJson(json.objects[i]);
        }
    }
};

export const canvasReviver = (json, element, isMobile) => {
    // CRITICAL: Fix text objects in the JSON BEFORE Fabric.js creates them
    console.log('[canvasReviver] Processing:', json.type);
    
    fixTextInJson(json);
    
    try {
        // Fabric enlivens custom objects with known type names (line, triangle).
        // Restore custom as a plain object from the original JSON.
        if (json.custom && typeof json.custom === 'object') {
            element.custom = json.custom;
        }

        if (element.custom?.type === 'line') {
            element.layoutManager = new ArrowLayoutManager();
        }

        customControlVisibility(element);
        setScaling(element, isMobile);
    } catch (e) {
        console.error('[canvasReviver] Error in reviver:', e);
        console.error('[canvasReviver] JSON that caused error:', json);
        throw e;
    }
};

export const getModifiedCounter = (canvas) => {
    if (!canvas) {
        return 0;
    }

    return canvas.get('modCount') ? canvas.get('modCount') : 0;
};

export const incModifiedCounter = (canvas) => {
    if (!canvas) {
        return;
    }

    canvas.set('modCount', getModifiedCounter(canvas) + 1);
};

export const restoreCanvas = (canvas, fromCanvas, isMobile, onComplete) => {
    canvas.remove(...canvas.getObjects());
    
    // Apply v5 to v6 migration to all objects before enlivening
    if (fromCanvas.objects) {
        fromCanvas.objects.forEach(obj => {
            migrate5to6(obj);
            migrate6to7(obj);
        });
    }
    
    // Fabric.js v6: util.enlivenObjects returns a Promise
    util.enlivenObjects(fromCanvas.objects, {
        reviver: (json, element) => canvasReviver(json, element, isMobile)
    }).then((objs) => {
        objs.forEach((item) => {
            canvas.add(item);
        });
        canvas.requestRenderAll(); // Make sure to call once we're ready!
        
        if (fromCanvas.backgroundImage && fromCanvas.backgroundImage.src !== canvas.backgroundImage?.src) {
            FabricImage.fromURL(fromCanvas.backgroundImage.src).then((img) => {
                img.set({originX: 'left', originY: 'top'});
                img.scaleToWidth(CANVAS_WIDTH);

                canvas.backgroundColor = '#191919';
                canvas.backgroundImage = img;
                canvas.renderAll();
            });
        }

        if (!fromCanvas.backgroundImage) {
            // In Fabric v6, backgroundImage and backgroundColor are properties
            canvas.backgroundImage = null;
            canvas.backgroundColor = fromCanvas.background;
        }

        onComplete && onComplete();
    }).catch((error) => {
        console.error('Error restoring canvas:', error);
        onComplete && onComplete();
    });
};